<template>
  <select-dict
    v-model="hardwareValueTypeCode"
    :select-first-item="true"
    :select-list="hardwareValues"
  />
</template>

<script setup lang="ts">
import { computed, defineModel, watch } from "vue";
import SelectDict from "@/components/UI/SelectDict.vue";
import { useDictStore } from "@/stores/dictStore";
import { useNewEstimateStore } from "@/stores/new/estimateStore";
import { useDoorsStore } from "@/stores/new/doors.store";

import {
  SteelSystemCodes,
  SlidingDoors,
  SteelFoldingDoorsCode,
  SteelMultiSlidingDoorsCode,
} from "@/app/constants";
import {
  PatchesSetTypeCode,
  HardwareNoneTypeCode,
  PatchesSetConcealedHingesCode,
  PatchesSetBulletHingesCode,
  SteelPatchesSetTypeCode,
} from "@/app/constants/hardwares";
import {
  hasCode,
  getDefaultHardwareValueCode,
  getDefaultHardwareValueCodeWithNone,
} from "@/utils/common";

type HardwareValueSelectProps = {
  hardwareTypeCode: string;
};

const props = defineProps<HardwareValueSelectProps>();
const hardwareValueTypeCode = defineModel<string>();

const dictStore = useDictStore();
const estimateStore = useNewEstimateStore();
const doorsStore = useDoorsStore();

const systemCode = computed(() => estimateStore.estimate.installedSystemCode);

const activeDoor = computed(() => doorsStore.activeDoor);
const doorType = computed(() => activeDoor.value?.structureType);

const isSteelPatchesSet = () => {
  return (
    SteelSystemCodes.includes(systemCode.value) &&
    props.hardwareTypeCode === PatchesSetTypeCode
  );
};

const isSlidingDoor = () =>
  [...SlidingDoors, SteelMultiSlidingDoorsCode].includes(doorType.value ?? "");
const isSteelFoldingDoor = () =>
  SteelFoldingDoorsCode.includes(doorType.value ?? "");

const getFilteredSteelPatchesList = (list) => {
  const filteredList = list.filter(
    (item) =>
      item.code.includes(SteelPatchesSetTypeCode) ||
      hasCode(item.code, HardwareNoneTypeCode),
  );
  return filteredList;
};

const hardwareValues = computed(() => {
  const list = dictStore.getHardwareValues(props.hardwareTypeCode);

  if (isSteelPatchesSet()) {
    return getFilteredSteelPatchesList(list);
  }
  return list.filter((item) => !SteelPatchesSetTypeCode.includes(item.code));
});

const setDefaultValueForSteelPatchesSet = (list) => {
  if (isSlidingDoor()) {
    hardwareValueTypeCode.value = getDefaultHardwareValueCodeWithNone(
      list,
      HardwareNoneTypeCode,
    );
    return;
  }

  if (isSteelFoldingDoor()) {
    hardwareValueTypeCode.value = getDefaultHardwareValueCodeWithNone(
      list,
      PatchesSetConcealedHingesCode,
    );
    return;
  }

  hardwareValueTypeCode.value = getDefaultHardwareValueCodeWithNone(
    list,
    PatchesSetBulletHingesCode,
  );
};

watch(
  () => hardwareValues.value,
  (list) => {
    if (!list.length) {
      return;
    }
    const current = hardwareValueTypeCode.value;
    const currentInList = list.some((item) => item.code === current);

    if (!isSteelPatchesSet() && !currentInList) {
      hardwareValueTypeCode.value = getDefaultHardwareValueCode(
        list,
        HardwareNoneTypeCode,
      );
      return;
    }

    isSteelPatchesSet() && setDefaultValueForSteelPatchesSet(list);
  },
  { immediate: true },
);
</script>
